// filepath: apps/web/src/features/admin/userRoles/OrphanPanel.tsx
//
// FR-UI-ADM-5 AC-8 — orphan side panel.
//
// Lists orphaned user_ids (users that disappeared from Keycloak
// since the last successful sync). Per-row "Remove role
// assignments" action calls DELETE /api/admin/users/{user_id}/
// roles with the current version.

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRemoveUserRoles } from "./queries";

interface OrphanPanelProps {
  orphans: string[];
  currentVersion: number;
  onClose: () => void;
}

export function OrphanPanel({
  orphans,
  currentVersion,
  onClose,
}: OrphanPanelProps): React.ReactElement {
  const { t } = useTranslation();
  return (
    <aside
      role="complementary"
      aria-label={t("admin.users.orphan_panel.title")}
      data-testid="orphan-panel"
    >
      <h2>{t("admin.users.orphan_panel.title")}</h2>
      {orphans.length === 0 ? (
        <p data-state="empty">{t("admin.users.orphan_panel.empty")}</p>
      ) : null}
      <ul>
        {orphans.map((uid) => (
          <OrphanRow
            key={uid}
            userId={uid}
            currentVersion={currentVersion}
            onCleaned={onClose}
          />
        ))}
      </ul>
      <button type="button" onClick={onClose} data-testid="orphan-panel-close">
        {t("common.close")}
      </button>
    </aside>
  );
}

function OrphanRow({
  userId,
  currentVersion,
  onCleaned,
}: {
  userId: string;
  currentVersion: number;
  onCleaned: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const remove = useRemoveUserRoles(userId);
  const [cleaned, setCleaned] = useState(false);

  if (cleaned) {
    return (
      <li data-user-id={userId} data-state="cleaned">
        {t("admin.users.orphan_panel.cleaned")}
      </li>
    );
  }

  return (
    <li data-user-id={userId}>
      <span>{userId}</span>
      <button
        type="button"
        aria-label={t("admin.users.orphan_panel.remove_action", { id: userId })}
        data-testid={`orphan-panel-remove-${userId}`}
        disabled={remove.isPending}
        onClick={async () => {
          try {
            await remove.mutateAsync(currentVersion);
            setCleaned(true);
            // Don't auto-close — the user may want to review more.
            void onCleaned;
          } catch {
            // error rendered below.
          }
        }}
      >
        {t("admin.users.orphan_panel.remove_action_short")}
      </button>
      {remove.isError ? (
        <span role="alert" data-state="remove_error">
          {remove.error?.message}
        </span>
      ) : null}
    </li>
  );
}
